import {useDiffEntriesDigestsWithFilter, useGetSelectedPathDigest, useProgressiveDiffActions} from '../stores/selectors'
import {updateURLHash} from '@github-ui/diffs/document-hash-helpers'
import {addUrlToHistoryStack} from '@github-ui/history'
import {useCallback} from 'react'

type UseDiffNavigation = {
  /** Function to navigate to the previous file */
  navigateToPrevious: () => void
  /** Function to navigate to the next file */
  navigateToNext: () => void
  /** Prefetch the previous file's diff data if not already loaded */
  prefetchPrevious: () => void
  /** Prefetch the next file's diff data if not already loaded */
  prefetchNext: () => void
}

/**
 * Hook that provides navigation functionality for navigating files in a pull request.
 *
 * Navigation should only operate on files that are visible in the file tree. The
 * store exposes both the full progressive entries and a filtered list derived from
 * the current file filter state; prefer the filtered list so "Next/Previous" skips
 * hidden files.
 *
 * @returns UseDiffNavigation object containing navigation functions and state
 */
export function useDiffNavigation(): UseDiffNavigation {
  // Get the filtered summaries and use the specialized selector to obtain the
  // progressive entries that correspond to the currently visible files.
  const diffEntries = useDiffEntriesDigestsWithFilter()
  const getSelectedPathDigest = useGetSelectedPathDigest()
  const {loadPriority} = useProgressiveDiffActions()

  const navigate = useCallback((pathDigest: string) => {
    updateURLHash(`diff-${pathDigest}`)
    addUrlToHistoryStack(window.location.href)
  }, [])

  const getAdjacentPathDigest = useCallback(
    (direction: 'previous' | 'next') => {
      if (diffEntries.length === 0) return undefined
      const selectedPathDigest = getSelectedPathDigest()
      const currentIndex = selectedPathDigest ? diffEntries.indexOf(selectedPathDigest) : -1

      let targetIndex: number
      if (direction === 'previous') {
        targetIndex = currentIndex <= 0 ? diffEntries.length - 1 : currentIndex - 1
      } else {
        targetIndex = currentIndex >= diffEntries.length - 1 || currentIndex < 0 ? 0 : currentIndex + 1
      }
      return diffEntries[targetIndex]
    },
    [diffEntries, getSelectedPathDigest],
  )

  const navigateToPrevious = useCallback(() => {
    const pathDigest = getAdjacentPathDigest('previous')
    if (pathDigest) navigate(pathDigest)
  }, [getAdjacentPathDigest, navigate])

  const navigateToNext = useCallback(() => {
    const pathDigest = getAdjacentPathDigest('next')
    if (pathDigest) navigate(pathDigest)
  }, [getAdjacentPathDigest, navigate])

  const prefetchPrevious = useCallback(() => {
    const pathDigest = getAdjacentPathDigest('previous')
    if (pathDigest) loadPriority(pathDigest)
  }, [getAdjacentPathDigest, loadPriority])

  const prefetchNext = useCallback(() => {
    const pathDigest = getAdjacentPathDigest('next')
    if (pathDigest) loadPriority(pathDigest)
  }, [getAdjacentPathDigest, loadPriority])

  return {
    navigateToPrevious,
    navigateToNext,
    prefetchPrevious,
    prefetchNext,
  }
}
